import { faPenToSquare, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Table } from 'components';
import { Button, TextField } from 'components/_form';
import { Modal } from 'components/Modal/Modal';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toast } from 'react-toastify';

const RowActions = styled(Flex)`
  gap: 8px;
  justify-content: center;
`;

export interface NumberNameItem {
  id: number;
  name: string;
  number: number;
}

interface Props {
  items: NumberNameItem[];
  onReload: () => void;
  labels: {
    add: string;
    edit: string;
    name: string;
    number: string;
  };
  onCreate: (data: { name: string; number: number }) => Promise<boolean>;
  onUpdate: (id: number, data: { name: string; number: number }) => Promise<boolean>;
  onDelete: (id: number) => Promise<boolean>;
}

export const NumberNameResourcePanel: React.FC<Props> = ({
  items,
  onReload,
  labels,
  onCreate,
  onUpdate,
  onDelete
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<NumberNameItem>();
  const [name, setName] = useState('');
  const [number, setNumber] = useState('1');

  const sorted = useMemo(
    () => [...items].sort((a, b) => a.number - b.number || a.name.localeCompare(b.name)),
    [items]
  );

  const openAdd = () => {
    setEditingItem(undefined);
    setName('');
    setNumber(String((items.length || 0) + 1));
    setIsModalOpen(true);
  };

  const openEdit = (item: NumberNameItem) => {
    setEditingItem(item);
    setName(item.name);
    setNumber(String(item.number));
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    const payload = { name: name.trim(), number: Number(number) };

    if (!payload.name || Number.isNaN(payload.number)) {
      toast.error(t('common.fieldRequired'));
      return;
    }

    const ok = editingItem
      ? await onUpdate(editingItem.id, payload)
      : await onCreate(payload);

    if (!ok) return;

    setIsModalOpen(false);
    onReload();
  };

  const handleDelete = async (id: number) => {
    const ok = await onDelete(id);
    if (ok) onReload();
  };

  const header = [labels.number, labels.name, ''];

  const data = sorted.map((item) => ({
    number: item.number,
    name: item.name,
    actions: (
      <RowActions key={`actions-${item.id}`}>
        <Button
          variant="eucalyptus"
          icon={<FontAwesomeIcon icon={faPenToSquare} />}
          onClick={() => openEdit(item)}
        />
        <Button
          variant="red"
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => handleDelete(item.id)}
        />
      </RowActions>
    )
  }));

  return (
    <>
      <Flex justifyContent="flex-end" mb={2}>
        <Button variant="eucalyptus" onClick={openAdd}>
          <FontAwesomeIcon icon={faPlusCircle} /> {labels.add}
        </Button>
      </Flex>

      <Table header={header} data={data} sortTable={false} rightFixedKeys={[2]} />

      <Modal
        isOpen={isModalOpen}
        onCancelClick={() => setIsModalOpen(false)}
        headerTitle={editingItem ? labels.edit : labels.add}
        mainButton={{
          action: handleSave,
          variant: 'eucalyptus',
          label: t('buttons.save')
        }}
        isSecondButtonVisible
        gridTemplateColumns="70%"
      >
        <Flex flexDirection="column" gap="12px">
          <TextField
            label={labels.number}
            type="number"
            value={number}
            onChange={(e) => setNumber(e.target.value)}
          />
          <TextField
            label={labels.name}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </Flex>
      </Modal>
    </>
  );
};
